/**
 * PolicyDocumentsPage — Conformara Policy-AI Slice 1 (PRD §5.3).
 * Role-gated (ADMIN / SAFETY_MANAGER) document library: upload PDF/DOCX,
 * status polling while PROCESSING, download / reprocess / edit / delete.
 * READY documents power the assistant and question references.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Upload, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { api } from '@/utils/api';

interface PolicyDocument {
  id: number;
  title: string;
  fileName: string;
  docType: 'COMPANY_POLICY' | 'PAID_STANDARD' | 'REGULATION' | 'OTHER';
  status: 'UPLOADED' | 'PROCESSING' | 'READY' | 'FAILED';
  fileSizeBytes: number;
  pageCount?: number | null;
  sectionCount?: number | null;
  errorMessage?: string | null;
  uploadedBy?: { id: number; firstName: string; lastName: string } | null;
}

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const ACCEPTED_MIMES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const DOC_TYPE_LABELS: Record<string, string> = {
  COMPANY_POLICY: 'Company policy',
  PAID_STANDARD: 'Paid standard',
  REGULATION: 'Regulation',
  OTHER: 'Other',
};

const STATUS_BADGE: Record<string, string> = {
  UPLOADED: 'bg-slate-700 text-slate-200',
  PROCESSING: 'bg-amber-900/60 text-amber-200',
  READY: 'bg-emerald-900/60 text-emerald-200',
  FAILED: 'bg-red-900/60 text-red-200',
};

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_MIMES.includes(file.type)) {
    return 'Only text-layer PDF or DOCX files are accepted';
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return 'File is larger than the 50 MB limit';
  }
  return null;
}


function badgeText(status: PolicyDocument['status']): string {
  if (status === 'PROCESSING' || status === 'UPLOADED') return 'Extracting…';
  return status;
}

export function PolicyDocumentsPage() {
  const [docs, setDocs] = useState<PolicyDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('COMPANY_POLICY');
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/policy-documents');
      setDocs(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load your document library.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // PRD §5.3: auto-poll /status every 3 s while any row is PROCESSING.
  const anyProcessing = useMemo(() => docs.some((d) => d.status === 'PROCESSING'), [docs]);
  const docsRef = useRef<PolicyDocument[]>(docs);
  docsRef.current = docs;
  useEffect(() => {
    if (!anyProcessing) {
      return undefined;
    }
    let timer: ReturnType<typeof setInterval> | undefined;
    const poll = async (): Promise<void> => {
      const processing = docsRef.current.filter((d) => d.status === 'PROCESSING');
      if (!processing.length) {
        return;
      }
      const results: Array<null | { id: number; status: PolicyDocument['status']; pageCount: number | null; sectionCount: number | null; errorMessage: string | null }> =
        await Promise.all(
          processing.map((d) =>
            api
              .get(`/policy-documents/${d.id}/status`)
              .then((r) => ({ id: d.id, ...r.data }))
              .catch(() => null)
          )
        );
      setDocs((prev) =>
        prev.map((d) => {
          const r = results.find((x) => x && x.id === d.id);
          return r
            ? {
                ...d,
                status: r.status,
                pageCount: r.pageCount,
                sectionCount: r.sectionCount,
                errorMessage: r.errorMessage,
              }
            : d;
        })
      );
    };
    void poll();
    timer = setInterval(() => {
      void poll();
    }, 3000);
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [anyProcessing]);

  const uploadFile = async (file: File, title: string) => {
    const problem = validateFile(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploadError(null);
    const form = new FormData();
    form.append('file', file);
    form.append('title', title);
    form.append('docType', 'COMPANY_POLICY');
    try {
      await api.post('/policy-documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (e: any) {
      setUploadError(e?.response?.data?.message || 'Upload failed — try again.');
    }
  };

  const onFilesDropped = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    // The upload needs a title; default to the filename minus extension so a
    // drag-drop never blocks on a prompt.
    void uploadFile(file, file.name.replace(/\.[^.]+$/, '').slice(0, 200));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => onFilesDropped(e.target.files);

  const startEdit = (d: PolicyDocument) => {
    setEditing(d.id);
    setEditTitle(d.title);
    setEditType(d.docType);
    setConfirmDelete(null);
  };

  const saveEdit = async (id: number) => {
    try {
      await api.patch(`/policy-documents/${id}`, { title: editTitle, docType: editType });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not save changes.');
    }
  };

  const remove = async (id: number) => {
    try {
      await api.delete(`/policy-documents/${id}`);
      setConfirmDelete(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not delete this document.');
    }
  };

  const reprocess = async (id: number) => {
    try {
      await api.post(`/policy-documents/${id}/reprocess`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Reprocess failed.');
    }
  };

  // Download endpoint is token-auth, so fetch the blob through the api client
  // (which carries the bearer token) and save it from the object URL.
  const downloadDocument = useCallback(async (id: number, fileName: string) => {
    try {
      const res = await api.get(`/policy-documents/${id}/download`, { responseType: 'blob' });
      const blob: Blob = res instanceof Blob ? res : ((res as { data?: Blob }).data as Blob);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Download failed — you may not have access to this document.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        {/* Mobile-friendly header: stacks on small screens, truncates long titles */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">Document Library</h1>
            <p className="text-sm text-slate-400">
              Company policies and standards — READY documents power the safety assistant and
              audit question references.
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium shrink-0"
          >
            <Upload className="h-4 w-4" /> Upload document
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-900/40 text-red-200 text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Upload zone */}
        <div
          data-testid="upload-zone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            onFilesDropped(e.dataTransfer.files);
          }}
          className={`mb-6 rounded-xl border-2 border-dashed p-6 text-center text-sm transition-colors ${
            dragActive ? 'border-sky-400 bg-sky-950/40' : 'border-slate-700 bg-slate-800/40'
          }`}
        >
          <p className="text-slate-300">
            Drag &amp; drop a <strong>PDF</strong> or <strong>DOCX</strong> here, or{' '}
            <button
              className="text-sky-400 hover:text-sky-300 underline"
              onClick={() => fileInputRef.current?.click()}
            >
              choose a file
            </button>
          </p>
          <p className="text-xs text-slate-500 mt-1">PDF or DOCX, up to 50 MB — text layer required (no scans).</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            data-testid="file-input"
            onChange={handleFileInput}
          />
        </div>
        {uploadError && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-amber-900/40 text-amber-200 text-sm" role="alert">
            {uploadError}
          </div>
        )}

        {/* Library table */}
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="rounded-xl bg-slate-800/40 p-8 text-center">
            <p className="text-slate-200 font-medium mb-2">No documents yet</p>
            <p className="text-sm text-slate-400 max-w-xl mx-auto">
              Upload your company policies and paid safety standards (PDF/DOCX). Once a document
              reaches READY status, its text powers the grounded safety assistant and can be
              attached as references to audit template questions.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/80 text-slate-400 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">File</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Size</th>
                  <th className="px-4 py-3 font-medium">Uploaded by</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {docs.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3 max-w-[220px]">
                      {editing === d.id ? (
                        <input
                          data-testid={`edit-title-${d.id}`}
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="block truncate font-medium text-slate-100">{d.title}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 max-w-[180px] truncate">{d.fileName}</td>
                    <td className="px-4 py-3">
                      {editing === d.id ? (
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value)}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                        >
                          {Object.entries(DOC_TYPE_LABELS).map(([v, label]) => (
                            <option key={v} value={v}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        DOC_TYPE_LABELS[d.docType] ?? d.docType
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`status-badge-${d.id}`}
                        title={d.status === 'FAILED' ? d.errorMessage || 'Extraction failed' : undefined}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[d.status] ?? 'bg-slate-700 text-slate-200'}`}
                      >
                        {badgeText(d.status)}
                      </span>
                      {d.status === 'READY' && (d.pageCount != null || d.sectionCount != null) && (
                        <span className="ml-2 text-xs text-slate-500">
                          {d.pageCount != null ? `${d.pageCount}p` : ''}
                          {d.pageCount != null && d.sectionCount != null ? ' · ' : ''}
                          {d.sectionCount != null ? `${d.sectionCount} sections` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{formatSize(d.fileSizeBytes)}</td>
                    <td className="px-4 py-3 text-slate-400 max-w-[140px] truncate">
                      {d.uploadedBy ? `${d.uploadedBy.firstName} ${d.uploadedBy.lastName}`.trim() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        {confirmDelete === d.id ? (
                          <>
                            <button
                              data-testid={`confirm-delete-${d.id}`}
                              onClick={() => remove(d.id)}
                              className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 text-xs text-white"
                            >
                              Confirm delete
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-xs"
                            >
                              Cancel
                            </button>
                          </>
                        ) : editing === d.id ? (
                          <>
                            <button
                              data-testid={`save-edit-${d.id}`}
                              onClick={() => saveEdit(d.id)}
                              className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-xs text-white"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditing(null)}
                              className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-xs"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              data-testid={`download-${d.id}`}
                              onClick={() => downloadDocument(d.id, d.fileName)}
                              title="Download"
                              className="inline-flex p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`reprocess-${d.id}`}
                              onClick={() => reprocess(d.id)}
                              title="Reprocess"
                              className="inline-flex p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`edit-${d.id}`}
                              onClick={() => startEdit(d)}
                              title="Edit title / type"
                              className="inline-flex p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`delete-${d.id}`}
                              onClick={() => setConfirmDelete(d.id)}
                              title="Delete"
                              className="inline-flex p-1.5 rounded text-slate-400 hover:text-red-300 hover:bg-slate-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PolicyDocumentsPage;